import React from 'react';
import { motion } from 'motion/react';

export function GridFeaturesSection() {
    const features = [
        {
            icon: '🔔',
            title: 'Smart Notifications',
            description: 'Stay informed with intelligent notifications that keep you updated without overwhelming you.',
        },
        {
            icon: '📅',
            title: 'Calendar & Reminders',
            description: 'Never miss important dates with integrated calendar and customizable reminder system.',
        },
        {
            icon: '👥',
            title: 'Team Management',
            description: 'Organize teams, assign roles, and manage permissions with ease.',
        },
        {
            icon: '📊',
            title: 'Analytics & Reports',
            description: 'Gain insights with comprehensive analytics and customizable reports.',
        },
        {
            icon: '🔒',
            title: 'Enterprise Security',
            description: 'Bank-level encryption and security protocols to keep your data safe.',
        },
        {
            icon: '⚡',
            title: 'Automation',
            description: 'Automate repetitive tasks and workflows to save time and reduce errors.',
        },
    ];

    // Stagger animation
    const getDelay = (idx) => {
        const pattern = [1, 0, 2, 4, 3, 5];
        return pattern.indexOf(idx) * 0.1;
    };

    return (
        <section className="py-10 sm:py-16 md:py-32 px-5" style={{ background: '#F5F5F7' }}>
            <div className="max-w-[1400px] mx-auto">
                {/* Header */}
                <motion.div
                    className="text-center mb-10 sm:mb-16 md:mb-20"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    <h2 className="text-[--color-primary] mb-6">Everything You Need</h2>
                    <p className="text-[--color-text-secondary]">Powerful features that make work effortless</p>
                </motion.div>

                {/* Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {features.map((feature, idx) => (
                        <FeatureCard key={idx} {...feature} delay={getDelay(idx)} />
                    ))}
                </div>
            </div>
        </section>
    );
}

function FeatureCard({ icon, title, description, delay }) {
    const [isHovered, setIsHovered] = React.useState(false);

    return (
        <motion.div
            className="bg-white rounded-3xl p-4 sm:p-6 md:p-12 cursor-pointer relative overflow-hidden"
            style={{
                boxShadow: isHovered
                    ? '0px 20px 60px rgba(9, 58, 91, 0.15)'
                    : '0px 4px 20px rgba(0, 0, 0, 0.06)',
                transform: isHovered ? 'translateY(-12px)' : 'translateY(0)',
                transition: 'all 400ms cubic-bezier(0.16, 1, 0.3, 1)',
            }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ delay, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/* Glow */}
            <motion.div
                className="absolute inset-0 bg-gradient-to-br from-[--color-primary]/5 to-transparent rounded-3xl"
                initial={{ opacity: 0 }}
                animate={{ opacity: isHovered ? 1 : 0 }}
                transition={{ duration: 0.3 }}
            />

            {/* Icon */}
            <motion.div
                // w-12 h-12 sm:w-14 sm:h-14 md:h-20 md:w-20
                className="flex items-center justify-center mb-4 sm:mb-5 md:mb-7 rounded-2xl feature-box"
                style={{
                    // width: '80px',
                    // height: '80px',
                    background: isHovered
                        ? 'linear-gradient(135deg, #093A5B 0%, #0F9D92 100%)'
                        : 'linear-gradient(135deg, #093A5B 0%, #0a4d6e 100%)',
                    boxShadow: isHovered
                        ? '0px 15px 40px rgba(9, 58, 91, 0.3)'
                        : '0px 10px 30px rgba(9, 58, 91, 0.2)',
                    transition: 'all 400ms cubic-bezier(0.16, 1, 0.3, 1)',
                }}
                animate={{
                    rotate: isHovered ? 5 : 0,
                    scale: isHovered ? 1.05 : 1,
                }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
                <motion.span className='text-2xl'
                    // style={{ fontSize: '2.5rem' }}
                    animate={{
                        scale: isHovered ? 1.1 : 1,
                        rotate: isHovered ? -5 : 0,
                    }}
                    transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                >
                    {icon}
                </motion.span>
            </motion.div>

            {/* Title */}
            <h3 className="text-[--color-primary] mb-4 relative">
                {title}

                <motion.div
                    className="absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-[--color-primary] to-[#0F9D92]"
                    initial={{ width: 0 }}
                    animate={{ width: isHovered ? '100%' : 0 }}
                    transition={{ duration: 0.3 }}
                />
            </h3>

            {/* Description */}
            <p className="text-[--color-text-secondary]" style={{ fontSize: '1.0625rem', lineHeight: '1.6' }}>
                {description}
            </p>

            {/* Learn more */}
            <motion.div
                className="mt-6 flex items-center gap-2 text-[--color-primary]"
                style={{ fontSize: '0.9375rem', fontWeight: 500 }}
                initial={{ opacity: 0, y: 10 }}
                animate={{
                    opacity: isHovered ? 1 : 0,
                    y: isHovered ? 0 : 10,
                }}
                transition={{ duration: 0.3 }}
            >
                <span>Learn more</span>
                <motion.span animate={{ x: isHovered ? 5 : 0 }} transition={{ duration: 0.3 }}>
                    →
                </motion.span>
            </motion.div>
        </motion.div>
    );
}
